"use client";

import Link from "next/link";
import React, { CSSProperties, useEffect, useState } from "react";
import "./style.css";

interface SocialLink {
  key: string;
  name: string;
  url: string;
}

interface SocialProps {
  style?: CSSProperties;
  showLabel?: boolean; // new prop to show label
}

const socialSvgs: Record<string, React.ReactElement> = {
  facebook: (
    <svg
      width="26"
      height="26"
      viewBox="0 0 26 26"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M13.0003 2.1665C7.01708 2.1665 2.16699 7.01659 2.16699 12.9998C2.16699 18.4068 6.12874 22.8885 11.3082 23.7021V16.1307H8.55649V12.9998H11.3082V10.6133C11.3082 7.89842 12.9245 6.39909 15.3999 6.39909C16.5851 6.39909 17.8244 6.61034 17.8244 6.61034V9.27534H16.4594C15.1128 9.27534 14.6936 10.1106 14.6936 10.9675V12.9998H17.6977L17.2177 16.1307H14.6936V23.7021C19.8719 22.8896 23.8337 18.4057 23.8337 12.9998C23.8337 7.01659 18.9836 2.1665 13.0003 2.1665Z"
        fill="currentColor"
      />
    </svg>
  ),
  twitter: (
    <svg
      width="26"
      height="26"
      viewBox="0 0 32 32"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M18.5326 13.9627L28.4429 2.69632H26.0944L17.4894 12.4788L10.6165 2.69632H2.68945L13.0826 17.4891L2.68945 29.3037H5.03801L14.1252 18.9731L21.3835 29.3037H29.3105L18.532 13.9627H18.5326ZM15.3159 17.6195L14.2629 16.1464L5.88422 4.42537H9.49146L16.2531 13.8846L17.3062 15.3577L26.0956 27.6533H22.4883L15.3159 17.62V17.6195Z"
        fill="currentColor"
      />
    </svg>
  ),
  telegram: (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="26"
      height="26"
      viewBox="0 0 496 512"
    >
      <path
        fill="currentColor"
        d="M248 8C111 8 0 119 0 256s111 248 248 248 248-111 248-248S385 8 248 8zm121.8 169.9l-40.7 191.8c-3 13.6-11.1 16.9-22.4 10.5l-62-45.7-29.9 28.8c-3.3 3.3-6.1 6.1-12.5 6.1l4.4-63.1 114.9-103.8c5-4.4-1.1-6.9-7.7-2.5l-142 89.4-61.2-19.1c-13.3-4.2-13.6-13.3 2.8-19.7l239.1-92.2c11.1-4 20.8 2.7 17.2 19.5z"
      />
    </svg>
  ),
  instagram: (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="26"
      height="26"
      viewBox="0 0 512 512"
    >
      <path
        fill="currentColor"
        d="M349.33 69.33a93.62 93.62 0 0193.34 93.34v186.66a93.62 93.62 0 01-93.34 93.34H162.67a93.62 93.62 0 01-93.34-93.34V162.67a93.62 93.62 0 0193.34-93.34h186.66m0-37.33H162.67C90.8 32 32 90.8 32 162.67v186.66C32 421.2 90.8 480 162.67 480h186.66C421.2 480 480 421.2 480 349.33V162.67C480 90.8 421.2 32 349.33 32z"
      />
      <path
        fill="currentColor"
        d="M377.33 162.67a28 28 0 1128-28 27.94 27.94 0 01-28 28zM256 181.33A74.67 74.67 0 11181.33 256 74.75 74.75 0 01256 181.33m0-37.33a112 112 0 10112 112 112 112 0 00-112-112z"
      />
    </svg>
  ),
  linkedin: (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="26"
      height="26"
      viewBox="50 50 430 430"
    >
      <path
        fill="currentColor"
        d="M417.2 64H96.8C79.3 64 64 76.6 64 93.9V415c0 17.4 15.3 32.9 32.8 32.9h320.3c17.6 0 30.8-15.6 30.8-32.9V93.9C448 76.6 434.7 64 417.2 64zM183 384h-55V213h55v171zm-25.6-197h-.4c-17.6 0-29-13.1-29-29.5 0-16.7 11.7-29.5 29.7-29.5s29 12.7 29.4 29.5c0 16.4-11.4 29.5-29.7 29.5zM384 384h-55v-93.5c0-22.4-8-37.7-27.9-37.7-15.2 0-24.2 10.3-28.2 20.3-1.5 3.6-1.9 8.5-1.9 13.5V384h-55V213h55v23.8c8-11.4 20.5-27.8 49.6-27.8 36.1 0 63.4 23.8 63.4 75.1V384z"
      />
    </svg>
  ),
  pinterest: (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="26"
      height="26"
      viewBox="0 0 512 512"
    >
      <path
        fill="currentColor"
        d="M256 32C132.3 32 32 132.3 32 256c0 91.5 54.7 170.2 133.3 205.1-.6-15.6-.1-34.3 3.9-51.2 4.3-18.1 28.8-122 28.8-122s-7.3-14.6-7.3-36.2c0-33.9 19.7-59.2 44.2-59.2 20.8 0 30.9 15.6 30.9 34.3 0 20.9-13.3 52.1-20.1 81-5.7 24.2 12.1 43.9 35.9 43.9 43.1 0 72.1-55.3 72.1-120.9 0-49.9-33.6-87.2-94.7-87.2-69 0-112 51.5-112 109 0 19.8 5.9 33.7 15.1 44.5 4.2 5 4.8 7 3.3 12.7-1.1 4.2-3.6 14.4-4.7 18.4-1.5 5.8-6.1 7.9-11.2 5.7-31.3-12.8-45.9-47-45.9-85.5 0-63.7 53.7-139.9 160.2-139.9 85.5 0 141.8 61.9 141.8 128.4 0 88-48.9 153.8-121 153.8-24.3 0-47.1-13.1-54.9-28 0 0-13.1 52.1-15.9 62.1-4.8 17.3-14.3 34.6-22.7 47.7 20.2 6 41.6 9.3 63.8 9.3 123.7 0 224-100.3 224-224S379.7 32 256 32z"
      />
    </svg>
  ),
  youtube: (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="26"
      height="26"
      viewBox="0 0 576 512"
    >
      <path
        fill="currentColor"
        d="M549.655 124.083c-6.281-23.65-24.787-42.276-48.284-48.597C458.781 64 288 64 288 64S117.22 64 74.629 75.486c-23.497 6.322-42.003 24.947-48.284 48.597-11.412 42.867-11.412 132.305-11.412 132.305s0 89.438 11.412 132.305c6.281 23.65 24.787 41.5 48.284 47.821C117.22 448 288 448 288 448s170.78 0 213.371-11.486c23.497-6.321 42.003-24.171 48.284-47.821 11.412-42.867 11.412-132.305 11.412-132.305s0-89.438-11.412-132.305zm-317.51 213.508V175.185l142.739 81.205-142.739 81.201z"
      />
    </svg>
  ),
};

export default function SocialIcons({ style, showLabel = false }: SocialProps) {
  const [socialLinks, setSocialLinks] = useState<SocialLink[]>([]);

  useEffect(() => {
    const fetchSocialLinks = async () => {
      try {
        const res = await fetch("/api/sidebar-data");
        if (!res.ok) throw new Error("Failed to fetch");
        const data = await res.json();
        setSocialLinks(data?.socialLinks || []);
      } catch (error) {
        console.error("Social icons fetch error:", error);
      }
    };
    fetchSocialLinks();
  }, []);

 
  return (
    <div className="social-icons" style={style}>
      {socialLinks.map((social) => {
        const svg = socialSvgs[social.key];
        if (!svg) return null;

        return (
          <Link
            key={social.key}
            className={`social-icon-link ${showLabel ? "with-label" : ""}`}
            href={social.url}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`coinccino ${social.name}`}
          >
            {svg}
            {showLabel && <span className="social-label">{social.name}</span>}
          </Link>
        );
      })}
    </div>
  );
}
